import { Header } from '@/components/DashboardComponents'
import { Button, RoundIconButton, Textbox } from '@/form/FormComponents'
import { useNavigate, useLoaderData } from 'react-router'
import { GenericEmptyListPlaceholder } from '@/DashboardLayout'
import { useModal, Modal, ModalContent, ModalButtons, useErrorModal } from '@/components/Modal'
import { useMenu } from '@/components/Menu'
import { Icon } from '@/components/Icon'
import { FormProvider, FormSubmitProvider } from '@/form/FormProvider'
import { publicAction } from '@/data/loaders'
import { useBreakpoint } from '@/hooks/useIsMobile'

export function Rubrics() {
  const navigate = useNavigate();
  const rubrics = useLoaderData().data.value;

  const hide_date = useBreakpoint((width) => width <= 600);

  return (
    <>
      <Header title="My rubrics" no_shadow={true}>
        <Button className="primary" icon="plus" text="New rubric" onClick={() => navigate("/rubrics/new")}/>
      </Header>
      <div className="dashboard_content_inner nearest_scrollable_container">
        <div className="scenario_list full_page_list" columns={2 - (hide_date ? 1 : 0)}>
          <div className="scenario_list_header">
            <div></div>
            <div>Title</div>
            {!hide_date && <div>Date created</div>}
            <div></div>
          </div>
          {rubrics.sort((a, b) => b.date_created - a.date_created).map((rubric) => <RubricItem key={rubric._id} rubric={rubric}/>)}
        </div>
        {rubrics.length == 0 ? <GenericEmptyListPlaceholder text="You haven't created any rubrics yet" inline={true}/> : null}
      </div>
    </>
  )
}

function RubricItem({rubric}) {
  const navigate = useNavigate();
  const [openErrorModal] = useErrorModal();

  const [openRenameRubricModal] = useModal(function ({ context, closeModal, setLoading }) {
    function submitRenameRubric({formatted_data, focusError}) {
      setLoading(true);
  
      publicAction("renameRubric", { id: context.rubric_id, title: formatted_data.title }).then(function(data) {
        closeModal();
        navigate('.', { replace: true });
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong, please try again."});
      });
    }

    return (
      <Modal title="Rename rubric">
        <FormProvider onSubmit={submitRenameRubric}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="title" onEnter={handleSubmit} default_value={context.rubric_title} required="Please enter a rubric title" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  function handleRenameRubric(rubric) {
    openRenameRubricModal({ rubric_id: rubric._id, rubric_title: rubric.title });
  }

  const [openTrashRubricModal] = useModal(function ({ context, closeModal, setLoading }) {
    function submitTrashRubric() {
      setLoading(true);
  
      publicAction("deleteRubric", { id: context.rubric_id }).then(function(data) {
        closeModal();
        navigate('.', { replace: true });
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title="Delete forever?">
        <ModalContent>
          {`This rubric will be deleted forever, skipping the trash. You can't undo this.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Confirm" className="destructive" onClick={submitTrashRubric}/>
        </ModalButtons>
      </Modal>
    )
  })

  function handleTrashRubric(rubric) {
    openTrashRubricModal({ rubric_id: rubric._id });
  }

  const [openCopyRubricModal] = useModal(function ({ context, closeModal, setLoading }) {
    function submitRenameRubric({formatted_data, focusError}) {
      setLoading(true);
  
      publicAction("duplicateRubric", { id: context.rubric_id, title: formatted_data.title }).then(function(data) {
        closeModal();
        navigate('.', { replace: true });
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong, please try again."});
      });
    }

    return (
      <Modal title="Make a copy of rubric">
        <FormProvider onSubmit={submitRenameRubric}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox title="New rubric title" name="title" onEnter={handleSubmit} default_value={"Copy of "+context.rubric_title} required="Please enter a rubric title" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  function handleCopyRubric(rubric) {
    openCopyRubricModal({ rubric_id: rubric._id, rubric_title: rubric.title });
  }

  const { handleContextMenu, handleOnClick } = useMenu({
    menuItems: [
      {
        label: 'Edit',
        icon: 'pencil-outline',
        onClick: () => navigate(`/rubrics/${rubric._id}`),
      },
      "separator",
      {
        label: 'Rename',
        icon: 'rename-outline',
        onClick: () => handleRenameRubric(rubric),
      },
      {
        label: 'Make a copy',
        icon: 'copy',
        onClick: () => handleCopyRubric(rubric),
      },
      "separator",
      {
        label: 'Delete forever',
        icon: 'delete-forever-outline',
        onClick: () => handleTrashRubric(rubric),
      }
    ]
  });

  const hide_date = useBreakpoint((width) => width <= 600);

  return (
    <div className="scenario_item" onClick={() => navigate("/rubrics/"+rubric._id)} onContextMenu={handleContextMenu} tabIndex={0}>
      <div className="item_icon">
        <Icon name="bullseye-arrow"/>
      </div>
      <div className="item_title">{rubric.title}</div>
      {!hide_date && <div className="item_date">{(new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(rubric.date_created))}</div>}
      <div className="item_more">
        <RoundIconButton icon="dots-vertical" className="small" onClick={handleOnClick} />
      </div>
    </div>
  )

}